import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps, GetStaticPropsResult } from 'next';
import { useTranslation } from 'next-i18next';

import { PUBLIC_IMAGES } from '../../../config';

import { Layout } from '@/components/Layout';
import { AdditionalServices } from '@/components/sections/AdditionalServices';
import { InstagramModule } from '@/components/sections/InstagramModule';
import { News } from '@/components/sections/News';
import { VisitInfo } from '@/components/sections/VisitInfo';
import { WhatToSee } from '@/components/sections/WhatToSee';
import { whatToSeeCardsData } from '@/components/WhatToSeeCard/whatToSeeCardsData';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { COMMON_LOCALE_PART } from '@/locale';
import { IPageWithMenuProps, INews, Locale, SiteMenuItem, ISEOOptions } from '@/types';
import { InstagramPost } from '@/types/types/Home';

interface IHomeProps extends IPageWithMenuProps {
  news: INews[];
  instagramFeed: InstagramPost[];
}

export default function Home({ siteMenu, news, instagramFeed }: IHomeProps) {
  const { t } = useTranslation();

  const seoOptions: ISEOOptions = {
    title: t('seo-text.title'),
    description: t('seo-text.home-page-description'),
    images: whatToSeeCardsData.map(({ image, titleKey }) => ({
      url: image,
      alt: t(titleKey) ?? '',
    })),
    thumbnail: `${process.env.SITE_DOMAIN}${PUBLIC_IMAGES.NEWS_BG}`,
  };

  return (
    <Layout siteMenu={siteMenu} seoOptions={seoOptions}>
      <h1 className="hidden">{t('seo-text.title')}</h1>
      <News news={news} />
      <WhatToSee siteMenu={siteMenu} />
      <AdditionalServices />
      <InstagramModule feed={instagramFeed} />
      <VisitInfo />
    </Layout>
  );
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
}

interface HomePageServerSideProps extends GetStaticProps {
  params: IParams;
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: false,
    paths: getI18nPaths(),
  };
};

export const getStaticProps = async ({
  params,
}: HomePageServerSideProps): Promise<GetStaticPropsResult<IHomeProps>> => {
  const { locale } = params;
  const request = api({ locale });

  const [{ data: siteMenu }, { data: news }, { data: instagramFeed }] = await Promise.all([
    request<SiteMenuItem[]>('site-menu/'),
    request<INews[]>('/dynamic-block/'),
    request<InstagramPost[]>('/instagram/posts/'),
  ]);

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu ?? []),
      news,
      instagramFeed,
    },
  };
};
